import React, { useEffect, useState } from "react";

import {
  AlertCircle,
  Check,
  CheckCircle2,
  Clock,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import {
  KPICard,
  CategoryPath,
  ConfidenceBadge,
  MappingReviewFilterBar,
  MappingReviewMappingsTable,
  MappingStatusBadge,
} from "@/components/catalog";
import { DashboardLayout } from "@/components/layout";
import { Button, DetailsSidebar, PageHeader, showToast } from "@/components/ui";
import {
  type MarketplaceMappingSessionsQuery,
  useAttributeReviewApproveSessionMutation,
} from "@/graphql/generated";
import { useMappingReview } from "@/hooks/useMappingReview";
import {
  formatProductType,
  formatProductTypeDomainName,
} from "@/lib/formatProductType";
import { cn } from "@/lib/utils";

function formatTimestamp(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="border-b border-slate-100 py-2 last:border-b-0 dark:border-slate-800">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <div
        className={cn(
          "mt-1 break-words text-sm text-slate-800 dark:text-slate-200",
          mono && "font-mono text-xs",
        )}
      >
        {value}
      </div>
    </div>
  );
}

export default function MappingReview() {
  const queryClient = useQueryClient();
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(
    null,
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const retryPublishMutation = useAttributeReviewApproveSessionMutation();

  const {
    filteredMappings,
    kpiCounts,
    marketplaces,
    isLoading,
    searchQuery,
    setSearchQuery,
    selectedMarketplace,
    setSelectedMarketplace,
    selectedStatus,
    setSelectedStatus,
    setStatusFilter,
    statusFilter,
    handleKPIClick,
    handleResetFilters,
    hasActiveFilters,
    pageInfo,
    setPage,
    totalCount,
  } = useMappingReview();

  const invalidateTasks = () =>
    queryClient.invalidateQueries({ queryKey: ["MarketplaceMappingSessions"] });

  const selectedMapping =
    filteredMappings.find((mapping) => mapping.id === selectedMappingId) ??
    null;
  const selectedProductTypeDomainName = selectedMapping
    ? formatProductTypeDomainName(selectedMapping.productType) ||
      formatProductTypeDomainName(selectedMapping.displayName) ||
      selectedMapping.displayName
    : null;
  const canRetrySelected =
    selectedMapping?.status === "failed" &&
    selectedMapping?.approvalSyncRetryable === true;
  // Under a server-side status filter the optimistically-changed row no longer
  // matches it, so refetch to drop it out; the unfiltered list stays local-only.
  const hasStatusFilter = statusFilter != null || selectedStatus !== "all";
  const reconcileFilteredList = () => {
    if (hasStatusFilter) {
      void queryClient.invalidateQueries({
        queryKey: ["MarketplaceMappingSessions"],
      });
    }
  };

  const handleRetryPublish = async () => {
    if (!selectedMapping) return;
    const mappingId = selectedMapping.id;
    const sessionId = selectedMapping.mappingSessionId ?? mappingId;
    try {
      await retryPublishMutation.mutateAsync({
        input: { sessionId, decision: "APPROVE" },
      });
      queryClient.setQueriesData<MarketplaceMappingSessionsQuery>(
        { queryKey: ["MarketplaceMappingSessions"] },
        (current) => {
          const sessions = current?.marketplaceMappingSessions;
          const index =
            sessions?.items.findIndex((item) => item.id === mappingId) ?? -1;
          if (
            !sessions ||
            index === -1 ||
            sessions.items[index].status !== "failed"
          ) {
            return current;
          }
          const nextItems = sessions.items.slice();
          nextItems[index] = { ...nextItems[index], status: "pending" };
          const counts = sessions.counts;
          return {
            ...current,
            marketplaceMappingSessions: {
              ...sessions,
              items: nextItems,
              ...(counts
                ? {
                    counts: {
                      ...counts,
                      failed: Math.max(0, counts.failed - 1),
                      pending: counts.pending + 1,
                    },
                  }
                : {}),
            },
          };
        },
      );
      reconcileFilteredList();
      showToast.success(
        "Retrying publish",
        "Re-running the approval publish. Refresh in a moment to see it complete.",
      );
    } catch (err) {
      showToast.error(
        "Retry failed",
        err instanceof Error ? err.message : "Could not retry the publish.",
      );
    }
  };

  useEffect(() => {
    if (!selectedMappingId) return;
    if (filteredMappings.some((mapping) => mapping.id === selectedMappingId)) {
      return;
    }
    setSelectedMappingId(null);
  }, [filteredMappings, selectedMappingId]);

  return (
    <DashboardLayout activePath="/catalog/mapping-review">
      <PageHeader
        title="Mapping Review"
        subtitle="Review and manage marketplace mapping sessions"
        breadcrumbs={[
          { label: "Catalog", active: false },
          { label: "Mapping Review", active: true },
        ]}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={invalidateTasks}
            disabled={isLoading}
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <KPICard
          title="Total"
          value={kpiCounts.total}
          icon={CheckCircle2}
          color="slate"
          onClick={() => handleKPIClick(null)}
          isActive={statusFilter === null && selectedStatus === "all"}
        />
        <KPICard
          title="Mapped"
          value={kpiCounts.mapped}
          icon={Check}
          color="emerald"
          onClick={() => handleKPIClick("mapped")}
          isActive={statusFilter === "mapped" || selectedStatus === "mapped"}
        />
        <KPICard
          title="Pending"
          value={kpiCounts.pending}
          icon={Clock}
          color="amber"
          onClick={() => handleKPIClick("pending")}
          isActive={statusFilter === "pending" || selectedStatus === "pending"}
        />
        <KPICard
          title="Needs Review"
          value={kpiCounts.needsReview}
          icon={AlertCircle}
          color="rose"
          onClick={() => handleKPIClick("needs_review")}
          isActive={
            statusFilter === "needs_review" || selectedStatus === "needs_review"
          }
        />
        <KPICard
          title="Failed"
          value={kpiCounts.failed}
          icon={XCircle}
          color="rose"
          onClick={() => handleKPIClick("failed")}
          isActive={statusFilter === "failed" || selectedStatus === "failed"}
        />
      </div>

      <MappingReviewFilterBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedMarketplace={selectedMarketplace}
        onMarketplaceChange={setSelectedMarketplace}
        marketplaces={marketplaces}
        selectedStatus={selectedStatus}
        onStatusChange={(value) => {
          setSelectedStatus(value);
          setStatusFilter(null);
        }}
        hasActiveFilters={hasActiveFilters}
        onResetFilters={handleResetFilters}
      />

      <div
        className={cn(
          "grid gap-6",
          sidebarCollapsed
            ? "xl:grid-cols-[minmax(0,1fr)_56px]"
            : "xl:grid-cols-[minmax(0,1fr)_380px]",
        )}
      >
        <div className="min-w-0">
          <MappingReviewMappingsTable
            mappings={filteredMappings}
            allCount={totalCount}
            isLoading={isLoading}
            selectedMappingId={selectedMappingId}
            onSelectMapping={(mapping) => {
              setSelectedMappingId(mapping.id);
              setSidebarCollapsed(false);
            }}
            onResetFilters={handleResetFilters}
            pageInfo={pageInfo}
            onPageChange={setPage}
          />
        </div>

        <DetailsSidebar
          title={selectedProductTypeDomainName ?? "Mapping session"}
          eyebrow="Mapping session"
          subtitle={
            selectedMapping
              ? `${selectedMapping.marketplace} / ${selectedMapping.productType}`
              : null
          }
          status={
            selectedMapping ? (
              <MappingStatusBadge status={selectedMapping.status} />
            ) : null
          }
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          emptyTitle="Select a mapping session"
          emptyDescription="Click a row to inspect session metadata and available actions."
        >
          {selectedMapping ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/40">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {selectedProductTypeDomainName}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {formatProductType(selectedMapping.productType) ||
                    selectedMapping.productType}
                </p>
              </div>

              <div className="rounded-lg border border-slate-200 px-3 dark:border-slate-800">
                <DetailRow
                  label="Session ID"
                  value={selectedMapping.mappingSessionId ?? selectedMapping.id}
                  mono
                />
                <DetailRow
                  label="Marketplace"
                  value={selectedMapping.marketplace}
                />
                <DetailRow
                  label="Product Type"
                  value={selectedMapping.productType}
                  mono
                />
                <DetailRow
                  label="Canonical Category"
                  value={
                    <CategoryPath path={selectedMapping.canonicalCategory} />
                  }
                />
                <DetailRow
                  label="Confidence"
                  value={
                    <ConfidenceBadge confidence={selectedMapping.confidence} />
                  }
                />
                <DetailRow
                  label="Blocked Listings"
                  value={selectedMapping.totalBlocked ?? "—"}
                />
                <DetailRow
                  label="Created"
                  value={formatTimestamp(selectedMapping.createdAt)}
                />
                <DetailRow
                  label="Updated"
                  value={formatTimestamp(selectedMapping.updatedAt)}
                />
                <DetailRow
                  label="Mapped At"
                  value={formatTimestamp(selectedMapping.mappedAt)}
                />
                <DetailRow
                  label="Mapped By"
                  value={selectedMapping.mappedBy ?? "—"}
                />
              </div>

              {canRetrySelected ? (
                <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Actions
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={handleRetryPublish}
                    disabled={retryPublishMutation.isPending}
                  >
                    <RefreshCw
                      className={cn(
                        "w-4 h-4",
                        retryPublishMutation.isPending && "animate-spin",
                      )}
                    />
                    Retry publish
                  </Button>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    The review is complete but publishing failed. Re-run the
                    publish — the approved fields are kept.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </DetailsSidebar>
      </div>
    </DashboardLayout>
  );
}
